import {
  DESIGN_AREA_OPTION_TYPE,
  DESIGN_AREA_PROPERTY,
  PLACEMENT_KEY,
  parseExpandedId,
  readPlacements,
  type Scene,
  type SceneElement,
  type ShopperPlacement,
} from '@pz/scene';
import { createAvatar } from './guide-avatar.js';
import * as layers from './layers.js';
import type {
  GuideApi,
  GuideOptions,
  GuidePhotoState,
  GuideSettings,
  GuideStepInfo,
  GuideStorage,
  ResolvedGuideSettings,
} from './guide-types.js';
import type { OptionDef, ProductDefinition, Values, WidgetConfig } from './types.js';

/** Optional inline field guidance, with transient outlines on the live preview. */

// ── What the guide says ──────────────────────────────────────────────────────

/**
 * The built-in Serbian. Every key may be replaced per product through
 * `display.guide.texts`; `{name}`, `{label}`, `{n}`, `{total}`, `{title}`,
 * `{price}`, `{px}` and `{seconds}` are substituted in both.
 */
export const GUIDE_TEXTS: Record<string, string> = {
  hello: 'Zdravo! Ja sam {name}. Klikni na bilo koje polje i pokazaću ti šta ono menja na dizajnu.',
  // one line per question type — what to do + what it changes
  'field.text': 'Upiši {label} — ispisuje se odmah na dizajnu.',
  'field.textarea': 'Upiši {label}. Možeš i u više redova — sve se ispisuje na dizajnu.',
  'field.dropdown': 'Izaberi {label} iz liste — dizajn se menja čim izabereš.',
  'field.swatch': 'Izaberi {label} — klikni na jednu od ponuđenih opcija.',
  'field.swatchMultiple': 'Izaberi {label} — možeš više opcija, klikni ponovo da poništiš.',
  'field.checkbox': 'Uključi ako želiš: {label}.',
  'field.date': 'Izaberi datum — na dizajnu se ispisuje u lepom formatu.',
  'field.color': 'Izaberi boju — deo dizajna odmah menja boju.',
  'field.image_upload':
    'Dodaj fotografiju (JPG ili PNG). Posle dodavanja možeš da je pomeriš i uvećaš prevlačenjem na pregledu.',
  'field.vector_upload': 'Dodaj vektorski fajl (SVG, PDF ili AI) — koristi se za precizan otisak.',
  'field.photo_collage':
    'Izaberi sve slike odjednom. Rasporedićemo ih automatski — posle ih u „Uredi raspored“ menjaš mestima i kadriraš.',
  'field.clipart_multi': 'Izaberi sličice iz kolekcije — biće raspoređene po dizajnu.',
  'field.ai_image': 'Opiši šta želiš i AI će nacrtati sliku u stilu ovog dizajna.',
  'field.design_area':
    'Ovde praviš svoj dizajn: dodaj tekst, sliku ili ukras i rasporedi ih u polju za štampu.',
  'field.map_location': 'Potraži mesto — mapa na dizajnu će pokazati baš to mesto.',
  'field.spotify_track': 'Potraži pesmu — naziv i Spotify kod idu na dizajn.',
  'field.audio_source':
    'Ovde biraš zvuk koji modul pušta: link, snimak ili fajl. Ne vidi se na dizajnu, ali se čuje 🎵',
  'field.voice_record': 'Snimi poruku svojim glasom — modul će je puštati.',
  'field.video_source': 'Nalepi link ka videu — zvuk iz njega ide u modul.',
  'field.other': 'Popuni {label}.',
  // rules appended after the field line, joined by a space
  'rule.required': 'Obavezno.',
  'rule.maxLength': 'Najviše {n} znakova.',
  'rule.letters': 'Samo slova.',
  'rule.numbers': 'Samo brojevi.',
  'rule.price': 'Doplata {price}.',
  'rule.maxFiles': 'Do {n} slika.',
  'rule.minSize': 'Bar {px} px da otisak bude oštar.',
  'rule.audioBudget': 'Ukupno do {seconds} s zvuka.',
  // after a change; the outline identifies the affected artwork
  'changed.text': 'Vidiš? Ispisuje se ovde 👉',
  'changed.image': 'Tvoja slika je ovde 👉 Prevuci je na pregledu da je nameštiš.',
  'changed.color': 'Boja je promenjena ovde 👉',
  'changed.choice': 'Izabrano — promena je ovde 👉',
  'changed.design': 'Tvoj dizajn je na proizvodu 👉',
  'changed.map': 'Mapa sada pokazuje to mesto 👉',
  'changed.song': 'Pesma je na dizajnu 👉',
  'changed.date': 'Datum je ispisan ovde 👉',
  'changed.moved': 'Pomereno ✓',
  'changed.audio': 'Zvuk je dodat ✓ Modul će ga puštati — na dizajnu se ne vidi.',
  'changed.generic': 'Zabeleženo ✓',
  photoBusy: 'Sređujem sliku…',
  photoReady: 'Gotovo! Prevuci sliku na pregledu da je nameštiš.',
  photoFailed: 'Slika je dodata, ali obrada nije uspela — možemo dalje.',
  step: 'Korak {n} od {total}.',
  stepTitled: 'Korak {n}: {title}',
  missing: 'Ovo polje je obavezno — bez njega ne možemo dalje.',
  nudge: 'Kad budeš spreman, popuni „{label}“ — obavezno je.',
  done: 'Sve je spremno! 🎉 Sada dodaj u korpu.',
  wizardDone: 'Sve je spremno! 🎉 Klikni „Sačuvaj“ pa dodaj u korpu.',
  'help.title': 'Pomoć',
  'help.note': 'Napomena',
  'help.showMe': 'Pokaži mi',
  'help.toggle': 'Prikaži vodiča',
  'help.toggleHint': 'Vodiča možeš ponovo uključiti dugmetom uz polja.',
  'help.generic': 'Klikni na polje i pokazaću ti šta ono menja na dizajnu.',
  'help.close': 'Zatvori',
  mini: 'Pokaži vodiča',
  hide: 'Sakrij vodiča',
  avatarLabel: 'Vodič {name} — pomoć',
};

/**
 * The bulleted advice in the help card, by kind of question.
 *
 * Not overridable per product on purpose: a shop that wants its own words has
 * `texts` for the sentence the bear actually says, and six lists of tips per
 * product is a settings screen nobody would ever fill in.
 */
export const GUIDE_TIPS: Record<string, string[]> = {
  text: ['Proveri velika i mala slova — ispisuje se tačno tako.', 'Emodži se ne štampaju.'],
  image: [
    'Najbolje: jasna, dobro osvetljena slika.',
    'Bez filtera i okvira.',
    'Lice neka bude celo u kadru.',
  ],
  audio: [
    'Zvuk se ne vidi na dizajnu — čuje se iz modula.',
    'Možeš da isečeš deo pesme i podesiš jačinu.',
  ],
  design: [
    'Polje sa isprekidanom linijom je ono što se štampa.',
    'Prevuci element da ga pomeriš, uglove da ga uvećaš.',
    'Sve što izađe iz polja neće biti odštampano.',
  ],
  map: ['Ukucaj grad ili adresu, pa izaberi iz liste.', 'Možeš uneti i koordinate.'],
  song: ['Ukucaj naziv pesme ili izvođača.', 'Kod se skenira u Spotify aplikaciji.'],
  choice: ['Klikni na opciju — promena se odmah vidi.'],
};

/**
 * Fills `{placeholders}` in a guide line.
 *
 * A placeholder with nothing to put in it is removed rather than left showing:
 * the alternative is a shopper reading "Popuni {label}", which is worse than a
 * slightly short sentence. Whitespace is tidied afterwards for the same reason.
 */
export function interpolate(
  text: string,
  vars: Record<string, string | number | undefined | null> = {},
): string {
  return text
    .replace(/\{(\w+)\}/g, (_match, key: string) => {
      const value = vars[key];
      return value === undefined || value === null ? '' : String(value);
    })
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([.,!?])/g, '$1')
    .trim();
}

// ── Settings: the shop, the product and the shopper each have a say ──────────

/** Where the shopper's own choice ("hide the bear") is remembered. */
export const GUIDE_STORAGE_KEY = 'pz-guide';
/** Where "already greeted" is remembered, for this browser tab only. */
export const GUIDE_HELLO_KEY = 'pz-guide-hello';

/** Only real overrides count: an empty box in Studio must not blank a line. */
function cleanTexts(texts: Record<string, string> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(texts ?? {})) {
    if (typeof value === 'string' && value.trim() !== '') out[key] = value.trim();
  }
  return out;
}

/**
 * The guide's settings after the connector, the Studio preview, the product,
 * the shop and the shopper have each had their say — in that order of
 * authority.
 *
 * The order is not arbitrary. `config.guide === 'off'` is the shop owner's
 * kill switch in WordPress and beats everything, including a product that asks
 * for the bear. The Studio preview frame (`config.preview.guide`) beats the
 * product so the owner can see the guide before turning it on for shoppers —
 * but never the shopper's own "hide it", which is why `shopperOff` is returned
 * separately instead of being folded into `enabled`: an enabled-but-hidden
 * guide still renders the little chip that brings it back.
 */
export function resolveGuideSettings(input: {
  config: WidgetConfig;
  product: ProductDefinition;
  storage?: GuideStorage;
}): ResolvedGuideSettings & { shopperOff: boolean } {
  const config = input.config as unknown as {
    guide?: unknown;
    preview?: { guide?: unknown };
  };
  const display = input.product?.display as unknown as { guide?: GuideSettings } | undefined;
  const guide = display?.guide;
  const theme = input.product?.theme ?? {};
  const mode = typeof config?.guide === 'string' ? config.guide : 'auto';
  const preview = typeof config?.preview?.guide === 'boolean' ? config.preview.guide : undefined;

  let enabled: boolean;
  if (mode === 'off') enabled = false;
  else if (preview !== undefined) enabled = preview;
  else if (mode === 'on') enabled = true;
  else if (typeof guide?.enabled === 'boolean') enabled = guide.enabled;
  else enabled = theme.guide === 'true';

  let shopperOff = false;
  try {
    shopperOff = input.storage?.get(GUIDE_STORAGE_KEY) === 'off';
  } catch {
    /* a browser with storage disabled simply has no opinion */
  }

  const name = (guide?.name?.trim() || theme.guideName?.trim() || 'Meda').slice(0, 40);
  return {
    enabled,
    shopperOff,
    name,
    position: guide?.position === 'left' ? 'left' : 'right',
    welcome: guide?.welcome !== false,
    nudges: guide?.nudges !== false,
    texts: cleanTexts(guide?.texts),
  };
}

// ── What to say about one question ───────────────────────────────────────────

/** The field line, the rules that apply to it, and the help card's tips. */
export interface GuideCopy {
  line: string;
  rules: string[];
  tips: string[];
}

export interface GuideCopyContext {
  /** The built-ins with the product's overrides already merged in. */
  texts?: Record<string, string>;
  /** The widget's own money formatter, so a surcharge reads like the page does. */
  formatPrice?: (amount: number) => string;
  /** The product's sound module, when it has one: the total sound budget. */
  audioProfile?: { maxSeconds?: number } | undefined;
}

/** option.type → the text key that explains it. */
const FIELD_KEYS: Record<string, string> = {
  text: 'field.text',
  textarea: 'field.textarea',
  dropdown: 'field.dropdown',
  swatch: 'field.swatch',
  checkbox: 'field.checkbox',
  date: 'field.date',
  color: 'field.color',
  image_upload: 'field.image_upload',
  vector_upload: 'field.vector_upload',
  photo_collage: 'field.photo_collage',
  clipart_multi: 'field.clipart_multi',
  ai_image: 'field.ai_image',
  design_area: 'field.design_area',
  map_location: 'field.map_location',
  spotify_track: 'field.spotify_track',
  audio_source: 'field.audio_source',
  voice_record: 'field.voice_record',
  video_source: 'field.video_source',
};

/** option.type → the tips list in the help card. */
const TIP_KEYS: Record<string, string> = {
  text: 'text',
  textarea: 'text',
  date: 'text',
  dropdown: 'choice',
  swatch: 'choice',
  checkbox: 'choice',
  color: 'choice',
  clipart_multi: 'choice',
  image_upload: 'image',
  vector_upload: 'image',
  photo_collage: 'image',
  ai_image: 'image',
  design_area: 'design',
  map_location: 'map',
  spotify_track: 'song',
  audio_source: 'audio',
  voice_record: 'audio',
  video_source: 'audio',
};

const AUDIO_TYPES = new Set(['audio_source', 'voice_record', 'video_source']);
const CHOICE_TYPES = new Set(['dropdown', 'swatch', 'checkbox', 'clipart_multi']);

/** The longest a bubble may get before it stops being one glance. */
export const BUBBLE_LIMIT = 160;

function num(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * What the bear says about one question, and what the help card shows.
 *
 * `line` is the product's own `config.guideText` when the shop wrote one, else
 * the built-in sentence for that type of question. The rules are appended
 * after it in a fixed order — required, length, allowed characters, surcharge,
 * how many pictures, how big they have to be, how much sound fits — because a
 * shopper reading a sentence twice should find the same words in the same
 * places.
 */
export function guideTextFor(option: OptionDef, ctx: GuideCopyContext = {}): GuideCopy {
  const texts = { ...GUIDE_TEXTS, ...(ctx.texts ?? {}) };
  const config = (option.config ?? {}) as Record<string, unknown>;
  const type = option.type;
  const say = (key: string, vars?: Record<string, string | number | undefined>): string =>
    interpolate(texts[key] ?? GUIDE_TEXTS[key] ?? '', { label: option.label, ...vars });

  let fieldKey = FIELD_KEYS[type] ?? 'field.other';
  if (type === 'swatch' && config.multiple === true) fieldKey = 'field.swatchMultiple';

  const custom = typeof config.guideText === 'string' ? config.guideText.trim() : '';
  const line = custom !== '' ? interpolate(custom, { label: option.label }) : say(fieldKey);

  const rules: string[] = [];
  if (option.required) rules.push(say('rule.required'));

  const maxLength = num(config.maxLength);
  if (maxLength !== undefined && maxLength > 0) rules.push(say('rule.maxLength', { n: maxLength }));

  const restrict = typeof config.restrict === 'string' ? config.restrict : '';
  if (restrict === 'letters' || restrict === 'lettersNoSpace') rules.push(say('rule.letters'));
  if (restrict === 'numbers' || restrict === 'numbersNoSpace') rules.push(say('rule.numbers'));

  if (option.priceDelta > 0 && ctx.formatPrice) {
    rules.push(say('rule.price', { price: ctx.formatPrice(option.priceDelta) }));
  }

  // How many pictures this question takes is not a number the author types: it
  // is the list of placeholders they pointed the field at (form.ts's
  // `targetElementIds`, which is what builds the multi-upload grid). A
  // `maxFiles` from an older import is honoured too.
  if (type === 'image_upload') {
    const targets = Array.isArray(config.targetElementIds) ? config.targetElementIds.length : 0;
    const slots = Math.max(targets, num(config.maxFiles) ?? 0);
    if (slots > 1) rules.push(say('rule.maxFiles', { n: slots }));
  }

  // The same two keys form.ts's `tooSmall()` refuses a photo by.
  const minSize = Math.max(num(config.minWidth) ?? 0, num(config.minHeight) ?? 0);
  if (minSize > 0 && (type === 'image_upload' || type === 'ai_image')) {
    rules.push(say('rule.minSize', { px: Math.round(minSize) }));
  }

  const seconds = num(ctx.audioProfile?.maxSeconds);
  if (AUDIO_TYPES.has(type) && seconds !== undefined && seconds > 0) {
    rules.push(say('rule.audioBudget', { seconds: Math.round(seconds) }));
  }

  const tips = GUIDE_TIPS[TIP_KEYS[type] ?? ''] ?? [];
  return { line, rules, tips: [...tips] };
}

/**
 * The sentence the bubble shows: the field line plus its rules, cut at a word
 * if it runs long. Two lines is the whole budget — a paragraph floating over a
 * product page is not read, it is dismissed.
 */
export function bubbleText(copy: GuideCopy, limit = BUBBLE_LIMIT): string {
  const full = [copy.line, ...copy.rules].filter((part) => part !== '').join(' ').trim();
  if (full.length <= limit) return full;
  const cut = full.slice(0, limit);
  const space = cut.lastIndexOf(' ');
  return `${(space > limit * 0.6 ? cut.slice(0, space) : cut).replace(/[\s.,;:]+$/, '')}…`;
}

/**
 * Which `changed.*` line fits an answer that just landed — chosen by what the
 * answer drives on the design first, and by the kind of question only when it
 * drives nothing the shopper can see.
 *
 * An empty string means "say nothing": an unbound text field mid-typing, where
 * "Zabeleženo ✓" after every keystroke would be noise.
 */
export function changedTextKey(option: OptionDef | undefined, property?: string): string {
  if (property === PLACEMENT_KEY) return 'changed.moved';
  const type = option?.type ?? '';
  // A design area is many layers at once — text, a photo, an ornament — so it
  // is never described by whichever of them changed last.
  if (property === DESIGN_AREA_PROPERTY || type === DESIGN_AREA_OPTION_TYPE) return 'changed.design';
  if (AUDIO_TYPES.has(type)) return 'changed.audio';
  if (property === 'image') return 'changed.image';
  if (property === 'color' || property === 'fill') return 'changed.color';
  if (property === 'text' && (type === 'text' || type === 'textarea')) return 'changed.text';
  if (type === 'map_location') return 'changed.map';
  if (type === 'spotify_track') return 'changed.song';
  if (type === 'date') return 'changed.date';
  if (CHOICE_TYPES.has(type)) return 'changed.choice';
  if (property === 'text') return 'changed.text';
  if (!property && (type === 'text' || type === 'textarea')) return '';
  return 'changed.generic';
}

// ── Where the answer landed on the preview ───────────────────────────────────

/** A box inside the preview container, in its own coordinates. */
export interface GuideSpot {
  left: number;
  top: number;
  width: number;
  height: number;
  /** Degrees, clockwise — the layer's own rotation. */
  rotation: number;
}

/**
 * Where one scene element is drawn, in the preview container's coordinates —
 * the same canvas→screen conversion the on-canvas photo editor does
 * (canvas-edit.ts), for the same reason: the canvas is centred on a phone sheet
 * and glued to a corner on the desktop, so only its measured rect can say where
 * a layer actually is.
 *
 * Shopper placements are applied exactly as the renderer applies them
 * (`applyShopperPlacements`): offsets in units of the AUTHORED size, and only
 * for layers whose author opened them up. A spotlight that ignored the gate
 * would sit next to the layer instead of on it whenever a stale offset survived
 * in the answers.
 */
export function elementSpot(
  scene: Scene | undefined,
  elementId: string,
  canvasRect: { left: number; top: number; width: number },
  boxRect: { left: number; top: number },
  placements: Record<string, ShopperPlacement> = {},
): GuideSpot | null {
  if (!scene || !scene.width || !canvasRect?.width) return null;
  const element = scene.elements?.find((el) => el.id === elementId);
  // A design area is not on the painted scene at all: it was replaced by the
  // shopper's own layers, whose ids are `<zone id>:<layer id>` — so pointing at
  // a zone means pointing at everything that came out of it.
  if (!element) return zoneSpot(scene, elementId, canvasRect, boxRect);
  if (!element.transform) return null;
  if (element.visible === false || element.printOnly) return null;

  const scale = canvasRect.width / scene.width;
  const t = element.transform;
  const place = placements[elementId];
  const moves = Boolean(element.shopper?.move) && place !== undefined;
  const grow = element.shopper?.scale && place?.scale ? place.scale : 1;
  const w = t.w * grow;
  const h = t.h * grow;
  const cx = t.cx + (moves ? (place?.dx ?? 0) * t.w : 0);
  const cy = t.cy + (moves ? (place?.dy ?? 0) * t.h : 0);
  return {
    left: canvasRect.left - boxRect.left + (cx - w / 2) * scale,
    top: canvasRect.top - boxRect.top + (cy - h / 2) * scale,
    width: w * scale,
    height: h * scale,
    rotation: t.rotation ?? 0,
  };
}

/** The layers one design area expanded into, on the painted scene. */
export function expandedZoneLayers(scene: Scene | undefined, zoneId: string): SceneElement[] {
  return (scene?.elements ?? []).filter((el) => parseExpandedId(el.id)?.zoneId === zoneId);
}

/**
 * Where a design area is drawn: the box around everything the shopper put in
 * it.
 *
 * Not the zone rectangle itself — that is the print area, and outlining the
 * whole of it when the shopper has written two words in the middle tells them
 * nothing about what their answer just did. The union of the layers is the
 * thing that changed. An empty design has no box, and gets no spotlight.
 *
 * The layers' own rotation is deliberately dropped: four boxes at four angles
 * have no single rotation, and an upright ring around all of them reads
 * correctly where a tilted one would look like a mistake.
 */
function zoneSpot(
  scene: Scene,
  zoneId: string,
  canvasRect: { left: number; top: number; width: number },
  boxRect: { left: number; top: number },
): GuideSpot | null {
  const layersOf = expandedZoneLayers(scene, zoneId).filter(
    (el) => el.transform && el.visible !== false && !el.printOnly,
  );
  if (layersOf.length === 0) return null;
  const scale = canvasRect.width / scene.width;
  let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;
  for (const el of layersOf) {
    const t = el.transform;
    // The drawn extent of a turned box, which is what the ring has to cover.
    const rad = ((t.rotation ?? 0) * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad)), sin = Math.abs(Math.sin(rad));
    const w = t.w * cos + t.h * sin;
    const h = t.w * sin + t.h * cos;
    left = Math.min(left, t.cx - w / 2);
    top = Math.min(top, t.cy - h / 2);
    right = Math.max(right, t.cx + w / 2);
    bottom = Math.max(bottom, t.cy + h / 2);
  }
  return {
    left: canvasRect.left - boxRect.left + left * scale,
    top: canvasRect.top - boxRect.top + top * scale,
    width: (right - left) * scale,
    height: (bottom - top) * scale,
    rotation: 0,
  };
}

// ── What changed ─────────────────────────────────────────────────────────────

function stable(value: unknown): string {
  try {
    return JSON.stringify(value) ?? 'undefined';
  } catch {
    return String(value);
  }
}

/**
 * The answer keys whose value differs between two snapshots, in the order they
 * appear in the new one.
 *
 * The widget's own bookkeeping keys (`__something`) are skipped — nobody wants
 * to be told about them — except `__placement`, which IS a shopper action: they
 * dragged a layer across the preview and deserve a "Pomereno ✓".
 */
export function changedKeys(prev: Values, next: Values): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const key of Object.keys(next ?? {})) {
    seen.add(key);
    if (!speakableKey(key)) continue;
    if (stable(prev?.[key]) !== stable(next[key])) out.push(key);
  }
  // An answer that was cleared is a change too — the layer it drove went blank.
  for (const key of Object.keys(prev ?? {})) {
    if (seen.has(key) || !speakableKey(key)) continue;
    if (stable(prev[key]) !== 'undefined') out.push(key);
  }
  return out;
}

function speakableKey(key: string): boolean {
  return !key.startsWith('__') || key === PLACEMENT_KEY;
}

/** Which movable layer the shopper just dragged, out of two placement maps. */
export function movedElementId(prev: Values, next: Values): string | undefined {
  const before = readPlacements((prev ?? {}) as Record<string, unknown>);
  const after = readPlacements((next ?? {}) as Record<string, unknown>);
  let last: string | undefined;
  for (const [id, place] of Object.entries(after)) {
    if (stable(before[id]) !== stable(place)) last = id;
  }
  return last;
}

// ── The state machine ────────────────────────────────────────────────────────

export type GuideMood = 'idle' | 'talk' | 'point' | 'happy' | 'wave' | 'busy';

/** What guidance identifies: artwork, a form row, or no target. */
export interface GuideTarget {
  kind: 'element' | 'row' | 'none';
  elementId?: string;
  optionId?: string;
}

export const NO_TARGET: GuideTarget = { kind: 'none' };

export interface GuideState {
  /** The question the shopper is in, or null. */
  focused: string | null;
  /** What the bubble is showing. */
  text: string;
  mood: GuideMood;
  target: GuideTarget;
  /** The bubble is folded away (the bear is just standing there). */
  collapsed: boolean;
  /** The welcome has been said in this tab. */
  greeted: boolean;
  /** Everything required is answered; reset when it stops being true. */
  completed: boolean;
  /** Questions already nudged about, at most once each per session. */
  nudged: string[];
  /** The last `changed.*` key said, and when — the anti-chatter throttle. */
  lastChangeKey: string;
  lastSaidAt: number;
}

export function initialGuideState(): GuideState {
  return {
    focused: null,
    text: '',
    mood: 'idle',
    target: NO_TARGET,
    collapsed: true,
    greeted: false,
    completed: false,
    nudged: [],
    lastChangeKey: '',
    lastSaidAt: 0,
  };
}

/** Everything that can make the guide open its mouth. */
export type GuideEvent =
  | { type: 'mount' }
  | { type: 'focus'; optionId: string | null; copy?: GuideCopy; elementId?: string }
  | {
      type: 'changed';
      optionId: string;
      key: string;
      elementId?: string;
      /** A text field being typed into: the same line must not be re-said. */
      typing?: boolean;
    }
  | { type: 'completeness'; complete: boolean }
  | { type: 'photo'; optionId: string; phase: GuidePhotoState['phase']; message?: string; elementId?: string }
  | { type: 'step'; index: number; total: number; title?: string; optionId?: string; elementId?: string }
  | { type: 'missing'; optionId: string; label: string }
  | { type: 'done' }
  | { type: 'nudge'; optionId: string; label: string }
  | { type: 'revealed' };

/** The facts the reducer needs that are not in its own state. */
export interface GuideContext {
  /** Built-ins plus this product's overrides. */
  texts: Record<string, string>;
  name: string;
  now: number;
  welcome: boolean;
  nudges: boolean;
  /** A wizard step is on screen: "click Save" rather than "add to cart". */
  inWizard?: boolean;
  /** The help card is open; the bubble must not fold away under it. */
  helpOpen?: boolean;
}

export interface GuideOutcome {
  state: GuideState;
  /** The new bubble line, or null to leave what is showing alone. */
  say: string | null;
  /** Start (or restart) the spotlight pulse on the new target. */
  spot: boolean;
  /** Fold the bubble away after this long with nothing else happening. */
  collapseAfterMs?: number;
}

/** How long a mood shows before the bear settles back. */
export const MOOD_MS: Record<string, number> = { wave: 1300, happy: 1200 };
/** The bubble folds away this long after the last event, with no field focused. */
export const COLLAPSE_MS = 6000;
/** Quiet for this long with a required question empty → one nudge. */
export const NUDGE_MS = 12000;
/** `changed.*` never re-says faster than this. */
export const SAY_THROTTLE_MS = 900;

/**
 * The whole of the guide's behaviour, as one pure function.
 *
 * It is pure so that "what does the bear say when a photo finishes cutting out
 * while the shopper is in another field" is a test rather than an afternoon of
 * clicking. The shell does the looking-up (which option, which layer, what the
 * sentence is) and the moving; everything about *whether to speak at all* is
 * here, because that is the part that gets annoying when it is wrong.
 */
export function nextMessage(
  event: GuideEvent,
  state: GuideState,
  ctx: GuideContext,
): GuideOutcome {
  const text = (key: string, vars?: Record<string, string | number | undefined>): string =>
    interpolate(ctx.texts[key] ?? GUIDE_TEXTS[key] ?? '', { name: ctx.name, ...vars });
  const speak = (
    line: string,
    mood: GuideMood,
    target: GuideTarget,
    extra: Partial<GuideOutcome> = {},
  ): GuideOutcome => ({
    state: { ...state, text: line, mood, target, collapsed: false, lastSaidAt: ctx.now },
    say: line,
    spot: target.kind === 'element',
    ...extra,
  });
  const quiet = (patch: Partial<GuideState> = {}, extra: Partial<GuideOutcome> = {}): GuideOutcome => ({
    state: { ...state, ...patch },
    say: null,
    spot: false,
    ...extra,
  });

  switch (event.type) {
    case 'mount': {
      if (!ctx.welcome || state.greeted) return quiet({ collapsed: true, mood: 'idle' });
      const out = speak(text('hello'), 'wave', NO_TARGET, { collapseAfterMs: COLLAPSE_MS });
      out.state.greeted = true;
      return out;
    }

    case 'focus': {
      if (event.optionId === null) {
        // The text stays: a shopper who tabbed out of a field is often reading
        // the very sentence that explains it. It folds away on its own.
        return quiet(
          { focused: null, mood: 'idle', target: NO_TARGET },
          { collapseAfterMs: ctx.helpOpen ? undefined : COLLAPSE_MS },
        );
      }
      const target: GuideTarget = event.elementId
        ? { kind: 'element', elementId: event.elementId, optionId: event.optionId }
        : { kind: 'row', optionId: event.optionId };
      const line = event.copy ? bubbleText(event.copy) : state.text;
      // A question the guide has no words for (a type it does not know, in a
      // form it was handed mid-build) still gets an outline, not a blank explanation.
      if (line === '') return quiet({ focused: event.optionId, target }, { spot: target.kind === 'element' });
      const out = speak(line, target.kind === 'element' ? 'point' : 'talk', target);
      out.state.focused = event.optionId;
      return out;
    }

    case 'changed': {
      const target: GuideTarget = event.elementId
        ? { kind: 'element', elementId: event.elementId, optionId: event.optionId }
        : { kind: 'row', optionId: event.optionId };
      const sameLine = event.key === state.lastChangeKey;
      const tooSoon = ctx.now - state.lastSaidAt < SAY_THROTTLE_MS;
      // Nothing to say (an unbound text field mid-typing), or the same thing
      // said a moment ago: the outline still follows without another announcement.
      if (event.key === '' || (event.typing && sameLine) || (tooSoon && state.lastChangeKey !== '')) {
        return quiet({ target }, { spot: target.kind === 'element' });
      }
      if (event.key === 'changed.generic' && event.typing) return quiet({ target });
      const out = speak(text(event.key), target.kind === 'element' ? 'point' : 'talk', target, {
        collapseAfterMs: state.focused === null ? COLLAPSE_MS : undefined,
      });
      out.state.lastChangeKey = event.key;
      return out;
    }

    case 'completeness': {
      if (!event.complete) return quiet({ completed: false });
      if (state.completed) return quiet();
      const out = speak(text(ctx.inWizard ? 'wizardDone' : 'done'), 'happy', NO_TARGET, {
        collapseAfterMs: COLLAPSE_MS,
      });
      out.state.completed = true;
      return out;
    }

    case 'done': {
      const out = speak(text(ctx.inWizard ? 'wizardDone' : 'done'), 'happy', NO_TARGET, {
        collapseAfterMs: COLLAPSE_MS,
      });
      out.state.completed = true;
      return out;
    }

    case 'photo': {
      if (event.phase === 'cutting') {
        return speak(event.message?.trim() || text('photoBusy'), 'busy', NO_TARGET);
      }
      if (event.phase === 'ready') {
        const target: GuideTarget = event.elementId
          ? { kind: 'element', elementId: event.elementId, optionId: event.optionId }
          : { kind: 'row', optionId: event.optionId };
        return speak(text('photoReady'), 'happy', target, { collapseAfterMs: COLLAPSE_MS });
      }
      return speak(text('photoFailed'), 'talk', NO_TARGET, { collapseAfterMs: COLLAPSE_MS });
    }

    case 'step': {
      const line = event.title
        ? text('stepTitled', { n: event.index + 1, total: event.total, title: event.title })
        : text('step', { n: event.index + 1, total: event.total });
      const target: GuideTarget = event.elementId
        ? { kind: 'element', elementId: event.elementId, optionId: event.optionId }
        : event.optionId
          ? { kind: 'row', optionId: event.optionId }
          : NO_TARGET;
      return speak(line, 'talk', target);
    }

    case 'missing': {
      return speak(text('missing', { label: event.label }), 'point', {
        kind: 'row',
        optionId: event.optionId,
      });
    }

    case 'nudge': {
      if (!ctx.nudges || state.focused !== null || state.nudged.includes(event.optionId)) {
        return quiet();
      }
      const out = speak(text('nudge', { label: event.label }), 'point', {
        kind: 'row',
        optionId: event.optionId,
      });
      out.state.nudged = [...state.nudged, event.optionId];
      out.collapseAfterMs = COLLAPSE_MS;
      return out;
    }

    case 'revealed': {
      const out = speak(text('help.generic'), 'wave', NO_TARGET, { collapseAfterMs: COLLAPSE_MS });
      out.state.greeted = true;
      return out;
    }

    default:
      return quiet();
  }
}

// ── The shell ────────────────────────────────────────────────────────────────

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

const EMPTY_BOX: Box = { left: 0, top: 0, width: 0, height: 0, right: 0, bottom: 0 };

function rectOf(el: Element | null | undefined): Box {
  const rect = (el as HTMLElement | null | undefined)?.getBoundingClientRect?.();
  if (!rect || typeof rect.width !== 'number') return EMPTY_BOX;
  const left = rect.left ?? 0;
  const top = rect.top ?? 0;
  const width = rect.width ?? 0;
  const height = rect.height ?? 0;
  return { left, top, width, height, right: rect.right ?? left + width, bottom: rect.bottom ?? top + height };
}

/** localStorage for the shopper's choice, sessionStorage for "already greeted". */
function browserStorage(): GuideStorage {
  const store = (key: string): Storage | undefined => {
    try {
      const web = key === GUIDE_HELLO_KEY ? sessionStorage : localStorage;
      return typeof web === 'undefined' ? undefined : web;
    } catch {
      // Safari in private mode throws on the property itself, not on the call.
      return undefined;
    }
  };
  return {
    get(key) {
      try {
        return store(key)?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        store(key)?.setItem(key, value);
      } catch {
        /* a full or disabled store is not worth a broken product page */
      }
    },
  };
}

/** Inline help travels with the form; only transparent outlines enter the preview. */
export function createGuide(options: GuideOptions): GuideApi {
  const settings = options.settings;
  const storage = options.storage ?? browserStorage();
  const clock = options.now ?? (() => Date.now());
  const texts = { ...GUIDE_TEXTS, ...cleanTexts(settings.texts) };
  const optionsById = new Map<string, OptionDef>();
  for (const option of options.product?.options ?? []) optionsById.set(option.id, option);

  let state = initialGuideState();
  state.greeted = safe(() => storage.get(GUIDE_HELLO_KEY), null) === '1';
  let off = safe(() => storage.get(GUIDE_STORAGE_KEY), null) === 'off';
  let mounted = false;
  let helpOpen = false;
  let stepIndex: number | null = null;
  let explainedOptionId: string | undefined;
  let snapshot: Values = {};
  let spotTarget: GuideTarget = NO_TARGET;
  const spots = new Map<string, HTMLElement>();

  const timers: Record<string, number | undefined> = {};
  const stops: Array<() => void> = [];
  let frame: number | undefined;

  // ── DOM ───────────────────────────────────────────────────────────────────
  const root = make('div', 'pz-guide');
  const bubble = make('div', 'pz-guide-bubble');
  const avatar = make('button', 'pz-guide-avatar') as HTMLButtonElement;
  const help = make('div', 'pz-guide-help');
  const mini = make('button', 'pz-guide-mini') as HTMLButtonElement;
  const dismiss = make('button', 'pz-guide-dismiss') as HTMLButtonElement;
  let helpBody: HTMLElement | undefined;
  let helpTitle: HTMLElement | undefined;
  let showMe: HTMLElement | undefined;
  let switchButton: HTMLElement | undefined;
  let guidedRow: HTMLElement | undefined;

  function make(tag: string, className: string): HTMLElement {
    const el = document.createElement(tag) as HTMLElement;
    el.className = className;
    return el;
  }

  function safe<T>(fn: () => T, fallback: T): T {
    try {
      const value = fn();
      return value === undefined ? fallback : value;
    } catch {
      return fallback;
    }
  }

  /**
   * `addEventListener` against anything that has one, remembered so `destroy`
   * can undo it. The target is `unknown` on purpose: the same helper wires the
   * form, the window, the visual viewport and our own buttons, and a stub DOM
   * in the tests has none of their real types.
   */
  function on(target: unknown, type: string, handler: (event: Event) => void, opts?: unknown): void {
    const node = target as
      | {
          addEventListener?: (t: string, h: unknown, o?: unknown) => void;
          removeEventListener?: (t: string, h: unknown, o?: unknown) => void;
        }
      | undefined;
    if (!node?.addEventListener) return;
    node.addEventListener(type, handler, opts);
    stops.push(() => node.removeEventListener?.(type, handler, opts));
  }

  function later(key: string, ms: number, fn: () => void): void {
    stop(key);
    timers[key] = window.setTimeout(() => {
      timers[key] = undefined;
      fn();
    }, ms) as unknown as number;
  }

  function stop(key: string): void {
    if (timers[key] !== undefined) window.clearTimeout(timers[key]);
    timers[key] = undefined;
  }

  function build(): void {
    bubble.setAttribute?.('role', 'status');
    bubble.setAttribute?.('aria-live', 'polite');
    bubble.hidden = true;
    avatar.type = 'button';
    avatar.setAttribute?.('aria-label', interpolate(texts.avatarLabel ?? '', { name: settings.name }));
    avatar.setAttribute?.('aria-expanded', 'false');
    avatar.appendChild?.(createAvatar());
    help.hidden = true;
    mini.type = 'button';
    mini.setAttribute?.('aria-label', texts.mini ?? '');
    mini.textContent = texts.mini ?? '';
    dismiss.type = 'button';
    dismiss.textContent = '×';
    dismiss.setAttribute?.('aria-label', texts.hide ?? 'Sakrij vodiča');
    on(dismiss, 'click', hideGuide);
    buildHelp();
    root.append?.(bubble, avatar, dismiss, help, mini);
    root.classList?.toggle('is-left', settings.position === 'left');
    root.classList?.toggle('is-off', off);
    root.classList?.toggle('is-coarse', safe(() => layers.pointerIsCoarse(), false));
    root.classList?.add('is-collapsed');
  }

  function buildHelp(): void {
    const head = make('div', 'pz-guide-help-head');
    head.appendChild?.(createAvatar());
    const name = make('span', 'pz-guide-help-name');
    name.textContent = settings.name;
    const close = make('button', 'pz-guide-help-close') as HTMLButtonElement;
    close.type = 'button';
    close.innerHTML = '&#10005;';
    close.setAttribute?.('aria-label', texts['help.close'] ?? 'Zatvori');
    on(close, 'click', () => closeHelp(true));
    head.append?.(name, close);

    helpTitle = make('div', 'pz-guide-help-title');
    helpBody = make('div', 'pz-guide-help-body');

    const actions = make('div', 'pz-guide-help-actions');
    showMe = make('button', 'pz-btn-ghost');
    (showMe as HTMLButtonElement).type = 'button';
    showMe.textContent = texts['help.showMe'] ?? '';
    on(showMe, 'click', () => {
      closeHelp(true);
      const optionId = state.focused ?? state.target.optionId ?? explainedOptionId;
      if (optionId) trackOption(optionId);
      place();
    });
    actions.appendChild?.(showMe);

    const foot = make('div', 'pz-guide-help-foot');
    switchButton = make('button', 'pz-guide-switch');
    (switchButton as HTMLButtonElement).type = 'button';
    switchButton.setAttribute?.('role', 'switch');
    switchButton.setAttribute?.('aria-checked', 'true');
    const switchLabel = make('span', 'pz-guide-switch-label');
    switchLabel.textContent = texts['help.toggle'] ?? '';
    switchButton.appendChild?.(switchLabel);
    on(switchButton, 'click', () => hideGuide());
    const hint = make('p', 'pz-guide-switch-hint');
    hint.textContent = texts['help.toggleHint'] ?? '';
    foot.append?.(switchButton, hint);

    help.append?.(head, helpTitle, helpBody, actions, foot);
  }

  // ── Saying things ─────────────────────────────────────────────────────────

  function apply(event: GuideEvent): void {
    if (off || !mounted) return;
    const out = nextMessage(event, state, {
      texts,
      name: settings.name,
      now: clock(),
      welcome: settings.welcome,
      nudges: settings.nudges,
      inWizard: stepIndex !== null,
      helpOpen,
    });
    const previousTarget = state.target;
    state = out.state;
    if (out.say !== null) setBubble(out.say);
    setMood(state.mood);
    if (out.spot || previousTarget.kind !== state.target.kind ||
        previousTarget.optionId !== state.target.optionId || previousTarget.elementId !== state.target.elementId) {
      setTarget(state.target, out.spot);
    }
    if (out.collapseAfterMs && !helpOpen) later('collapse', out.collapseAfterMs, collapse);
    else stop('collapse');
    if (helpOpen) fillHelp();
    schedule();
  }

  function setBubble(line: string): void {
    if (line === '') return;
    bubble.textContent = line;
    bubble.hidden = false;
    root.classList?.remove('is-collapsed');
  }

  function collapse(): void {
    if (helpOpen) return;
    state = { ...state, collapsed: true };
    bubble.hidden = true;
    root.classList?.add('is-collapsed');
  }

  const MOODS = ['is-talk', 'is-point', 'is-happy', 'is-wave', 'is-busy'];

  function setMood(mood: GuideMood): void {
    // Re-adding the class the bear already wears would restart its animation:
    // a wave that keeps waving because an unrelated event fired twice.
    if (mood !== 'idle' && safe(() => root.classList?.contains?.(`is-${mood}`) === true, false)) return;
    for (const one of MOODS) root.classList?.remove(one);
    if (mood !== 'idle') root.classList?.add(`is-${mood}`);
    const life = MOOD_MS[mood];
    if (life === undefined) {
      stop('mood');
      return;
    }
    // Waving and bouncing are one-shot; afterwards the bear goes back to
    // whatever it was doing, which is usually pointing at what it just said.
    later('mood', life, () => {
      root.classList?.remove(`is-${mood}`);
      if (state.target.kind === 'element') root.classList?.add('is-point');
    });
  }

  function setTarget(target: GuideTarget, pulse: boolean): void {
    markRow(target.optionId);
    if (target.kind === 'element' && pulse) flashSpot();
    // A ring already drawn keeps its 2.8s: "Sve je spremno! 🎉" arrives in the
    // same tick as the change that completed the form, and wiping the
    // spotlight the shopper is still looking at is not an improvement. Only a
    // move to a different question takes it away early.
    if (target.kind === 'row') clearSpot();
  }

  /** The row being explained wears `.is-guided` — briefly, and never two at once. */
  function markRow(optionId: string | undefined): void {
    const row = optionId ? safe(() => options.rowFor?.(optionId) ?? null, null) : null;
    if (guidedRow && guidedRow !== row) guidedRow.classList?.remove('is-guided');
    guidedRow = row ?? undefined;
    if (!row) {
      stop('guided');
      return;
    }
    row.classList?.add('is-guided');
    later('guided', 2500, () => {
      row.classList?.remove('is-guided');
      if (guidedRow === row) guidedRow = undefined;
    });
  }

  // ── The spotlight ─────────────────────────────────────────────────────────

  function clearSpot(): void {
    for (const box of spots.values()) box.remove?.();
    spots.clear();
    spotTarget = NO_TARGET;
    stop('spot');
  }

  /** Update geometry immediately; do not throttle a switch to a different field. */
  function flashSpot(): void {
    if (state.target.kind !== 'element') return;
    spotTarget = { ...state.target };
    paintSpots();
    later('spot', 2800, clearSpot);
  }

  function trackOption(optionId: string): void {
    if (!(options.product.bindings ?? []).some((binding) => binding.optionId === optionId)) return;
    spotTarget = { kind: 'element', optionId };
    paintSpots();
    later('spot', 2800, clearSpot);
  }

  function paintSpots(): void {
    const ids = spotTarget.kind !== 'element' ? [] :
      spotTarget.optionId && spotTarget.optionId !== PLACEMENT_KEY
        ? boundElements(spotTarget.optionId).map((binding) => binding.elementId)
        : spotTarget.elementId ? [spotTarget.elementId] : [];
    const visible = new Set<string>();
    for (const id of ids) {
      let box = spots.get(id);
      if (!box) {
        box = make('div', 'pz-guide-spot');
        box.setAttribute?.('aria-hidden', 'true');
        options.previewBox?.appendChild?.(box);
        spots.set(id, box);
      }
      // Mobile wizard styles make the preview position:static. Its actual
      // containing block can therefore be a scrollable ancestor of the preview.
      const geometry = spotGeometry(id, box.offsetParent as HTMLElement | null);
      if (!geometry) continue;
      visible.add(id);
      paintSpot(box, geometry);
    }
    for (const [id, box] of spots) {
      if (!visible.has(id)) { box.remove?.(); spots.delete(id); }
    }
  }

  function paintSpot(box: HTMLElement, geometry: GuideSpot): void {
    // Fractional CSS pixels matter on small previews; never inflate tiny layers.
    const style = box.style;
    style?.setProperty?.('left', `${geometry.left}px`);
    style?.setProperty?.('top', `${geometry.top}px`);
    style?.setProperty?.('width', `${geometry.width}px`);
    style?.setProperty?.('height', `${geometry.height}px`);
    style?.setProperty?.('transform', `rotate(${geometry.rotation}deg)`);
  }

  function spotGeometry(elementId: string, containingBlock?: HTMLElement | null): GuideSpot | null {
    const scene = safe(() => options.scene?.(), undefined);
    const canvasRect = rectOf(options.canvas);
    const anchor = containingBlock ?? options.previewBox;
    const boxRect = rectOf(anchor);
    // A collapsed strip or full-screen inspection should have no guide overlays.
    if (canvasRect.height < 80 || safe(() => options.canvasInLightbox(), false)) return null;
    // The scene is already resolved: applying placements twice would shift the pointer.
    return elementSpot(scene, elementId, canvasRect, {
      left: boxRect.left + (anchor.clientLeft || 0) - (anchor.scrollLeft || 0),
      top: boxRect.top + (anchor.clientTop || 0) - (anchor.scrollTop || 0),
    });
  }

  /** Coalesce resize/paint/scroll work, with a cancellable background-tab fallback. */
  function schedule(): void {
    if (frame !== undefined) return;
    const run = (): void => {
      if (frame !== undefined && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
      stop('layout');
      frame = undefined;
      place();
    };
    if (typeof requestAnimationFrame === 'function') {
      frame = requestAnimationFrame(run);
      later('layout', 120, run);
    } else {
      later('layout', 16, run);
    }
  }

  function place(): void {
    if (!mounted || off) return;
    paintSpots();
  }

  // ── Listening to the form ─────────────────────────────────────────────────

  /** The `.pz-row[data-option]` an event happened in, without `closest()`. */
  function rowOf(node: unknown): { row: HTMLElement; optionId: string } | null {
    let el = node as HTMLElement | null;
    let depth = 0;
    while (el && depth < 24) {
      const id = el.getAttribute?.('data-option');
      if (id) return { row: el, optionId: id };
      el = el.parentElement ?? null;
      depth += 1;
    }
    return null;
  }

  function focusOption(optionId: string | null): void {
    if (off || !mounted) return;
    // Re-entering the same field is a no-op — unless the bubble has already
    // folded away, in which case tapping the field again is the shopper asking
    // for the explanation back.
    if (optionId === state.focused && !state.collapsed) return;
    if (optionId === null) {
      clearSpot();
      apply({ type: 'focus', optionId: null });
      return;
    }
    explainedOptionId = optionId;
    const option = optionsById.get(optionId);
    apply({
      type: 'focus',
      optionId,
      copy: option
        ? guideTextFor(option, {
            texts,
            formatPrice: options.formatPrice,
            audioProfile: options.product?.audioProfile,
          })
        : undefined,
      elementId: boundElement(optionId)?.elementId,
    });
    trackOption(optionId);
  }

  /** All visible layers driven by the selected answer, on the painted side. */
  function boundElements(optionId: string): Array<{ elementId: string; property: string }> {
    const scene = safe(() => options.scene?.(), undefined);
    if (!scene) return [];
    const option = optionsById.get(optionId);
    const answer = safe(() => options.values?.()[optionId], undefined);
    const answers = Array.isArray(answer) ? answer : [answer];
    const picked = new Set(option?.values?.filter((v) => answers.includes(v.value)).map((v) => v.id));
    const found = new Map<string, { elementId: string; property: string }>();
    for (const binding of options.product?.bindings ?? []) {
      if (binding.optionId !== optionId) continue;
      if (binding.optionValueId && !picked.has(binding.optionValueId)) continue;
      // A design area's own element is never on a painted scene — the resolver
      // replaced it with the shopper's layers before anything drew it — so the
      // zone id counts as present when at least one of them is. `elementSpot`
      // turns it back into the box around them.
      if (binding.property === DESIGN_AREA_PROPERTY) {
        if (expandedZoneLayers(scene, binding.elementId).length === 0) continue;
        found.set(binding.elementId, { elementId: binding.elementId, property: binding.property });
        continue;
      }
      const element = scene.elements?.find((el) => el.id === binding.elementId);
      if (!element?.transform || element.visible === false || element.printOnly) continue;
      found.set(element.id, { elementId: element.id, property: binding.property });
    }
    return [...found.values()];
  }

  function boundElement(optionId: string): { elementId: string; property: string } | undefined {
    return boundElements(optionId)[0];
  }

  function resetIdle(): void {
    if (off || !mounted || !settings.nudges) return;
    later('idle', NUDGE_MS, () => {
      if (state.focused !== null || helpOpen) return;
      const waiting = requiredOptions().find(
        (o) => !safe(() => options.isAnswered?.(o.id) ?? false, false) && !state.nudged.includes(o.id),
      );
      if (waiting) apply({ type: 'nudge', optionId: waiting.id, label: waiting.label });
    });
  }

  /** The required questions the shopper can actually see right now. */
  function requiredOptions(): OptionDef[] {
    return (options.product?.options ?? []).filter((o) => {
      if (!o.required || o.type === 'divider') return false;
      const row = safe(() => options.rowFor?.(o.id) ?? null, null);
      return !row || row.hidden !== true;
    });
  }

  function isComplete(): boolean {
    const required = requiredOptions();
    if (required.length === 0) return false;
    return required.every((o) => safe(() => options.isAnswered?.(o.id) ?? false, false));
  }

  // ── The help card ─────────────────────────────────────────────────────────

  /** Closes the help card when the shopper taps anywhere else on the page. */
  let dismissHelp: (() => void) | undefined;

  function openHelp(): void {
    if (off || !mounted) return;
    helpOpen = true;
    help.hidden = false;
    avatar.setAttribute?.('aria-expanded', 'true');
    stop('collapse');
    fillHelp();
    schedule();
    if (dismissHelp || typeof document === 'undefined') return;
    const outside = (event: Event): void => {
      // The widget is in a shadow root, so a document-level listener sees the
      // HOST as the target; the composed path is the only honest answer to
      // "did they click the bear or the page behind it".
      const path = safe(() => (event as Event & { composedPath?: () => unknown[] }).composedPath?.(), undefined);
      const hit = Array.isArray(path) && path.length > 0 ? path[0] : (event as { target?: unknown }).target;
      if (!inside(hit)) closeHelp();
    };
    document.addEventListener?.('pointerdown', outside, true);
    dismissHelp = () => document.removeEventListener?.('pointerdown', outside, true);
    stops.push(() => dismissHelp?.());
  }

  /** True when a node is one of ours — the card, the bear or the bubble. */
  function inside(node: unknown): boolean {
    let el = node as HTMLElement | null;
    let depth = 0;
    while (el && depth < 24) {
      if (el === (root as HTMLElement)) return true;
      el = el.parentElement ?? null;
      depth += 1;
    }
    return false;
  }

  function closeHelp(restoreFocus = false): void {
    helpOpen = false;
    help.hidden = true;
    avatar.setAttribute?.('aria-expanded', 'false');
    if (restoreFocus) avatar.focus?.({ preventScroll: true });
    dismissHelp?.();
    dismissHelp = undefined;
    later('collapse', COLLAPSE_MS, collapse);
    schedule();
  }

  function fillHelp(): void {
    if (!helpBody || !helpTitle) return;
    const optionId = state.focused ?? state.target.optionId ?? explainedOptionId;
    const option = optionId ? optionsById.get(optionId) : undefined;
    helpTitle.textContent = option?.label ?? texts['help.title'] ?? '';
    helpBody.innerHTML = '';
    const copy = option
      ? guideTextFor(option, {
          texts,
          formatPrice: options.formatPrice,
          audioProfile: options.product?.audioProfile,
        })
      : { line: texts['help.generic'] ?? '', rules: [], tips: [] };

    const text = make('p', 'pz-guide-help-text');
    text.textContent = copy.line;
    helpBody.appendChild?.(text);
    if (copy.rules.length > 0) helpBody.appendChild?.(list('pz-guide-help-rules', copy.rules));
    if (copy.tips.length > 0) helpBody.appendChild?.(list('pz-guide-help-tips', copy.tips));
    if (option?.helpText) {
      const note = make('p', 'pz-guide-help-note');
      note.textContent = `${texts['help.note'] ?? ''}: ${option.helpText}`;
      helpBody.appendChild?.(note);
    }
    if (showMe) showMe.hidden = !optionId || boundElements(optionId).length === 0;
  }

  function list(className: string, items: string[]): HTMLElement {
    const ul = make('ul', className);
    for (const item of items) {
      const li = document.createElement('li');
      li.textContent = item;
      ul.appendChild?.(li);
    }
    return ul;
  }

  // ── Hidden and back again ─────────────────────────────────────────────────

  function hideGuide(): void {
    off = true;
    for (const key of Object.keys(timers)) stop(key);
    dismissHelp?.();
    dismissHelp = undefined;
    avatar.setAttribute?.('aria-expanded', 'false');
    helpOpen = false;
    help.hidden = true;
    bubble.hidden = true;
    switchButton?.setAttribute?.('aria-checked', 'false');
    root.classList?.add('is-off');
    clearSpot();
    markRow(undefined);
    safe(() => storage.set(GUIDE_STORAGE_KEY, 'off'), undefined);
    mini.focus?.({ preventScroll: true });
    schedule();
  }

  function showGuide(): void {
    off = false;
    switchButton?.setAttribute?.('aria-checked', 'true');
    root.classList?.remove('is-off');
    safe(() => storage.set(GUIDE_STORAGE_KEY, 'on'), undefined);
    apply({ type: 'revealed' });
    avatar.focus?.({ preventScroll: true });
    resetIdle();
  }

  // ── The API ───────────────────────────────────────────────────────────────

  return {
    get enabled(): boolean {
      return mounted && !off;
    },

    mount(): void {
      if (mounted) return;
      mounted = true;
      build();
      // Normal flow avoids covering artwork, fields, keyboards or theme cart bars.
      if (options.form.prepend) options.form.prepend(root);
      else options.form.appendChild?.(root);
      snapshot = { ...safe(() => options.values?.() ?? {}, {}) };

      // The form's own events. `focusin`/`focusout` bubble (unlike focus/blur),
      // and a swatch or an upload button is clicked rather than focused, which
      // is why pointerdown counts as entering a question too.
      on(options.form, 'focusin', (event) => {
        const hit = rowOf((event as { target?: unknown }).target);
        if (hit) focusOption(hit.optionId);
        resetIdle();
      });
      on(options.form, 'focusout', () => {
        later('blur', 150, () => {
          const active = safe(() => layers.activeElementDeep(), null);
          if (rowOf(active) || inside(active)) return;
          focusOption(null);
        });
      });
      on(options.form, 'pointerdown', (event) => {
        const hit = rowOf((event as { target?: unknown }).target);
        if (hit) focusOption(hit.optionId);
        resetIdle();
      });
      on(options.form, 'input', () => resetIdle());
      on(options.form, 'change', () => resetIdle());
      on(options.form, 'keydown', () => resetIdle());

      on(avatar, 'click', () => (helpOpen ? closeHelp() : openHelp()));
      on(mini, 'click', () => showGuide());
      on(root, 'keydown', (event) => {
        if ((event as KeyboardEvent).key === 'Escape' && helpOpen) closeHelp(true);
      });

      on(typeof window === 'undefined' ? undefined : window, 'resize', () => schedule());
      on(typeof window === 'undefined' ? undefined : window, 'scroll', () => {
        if (spotTarget.kind === 'element') schedule();
      }, { passive: true, capture: true });
      stops.push(safe(() => layers.observeSize(options.canvas, () => schedule()), () => undefined));
      stops.push(safe(() => layers.observeSize(options.previewBox, () => schedule()), () => undefined));

      apply({ type: 'mount' });
      if (state.greeted) safe(() => storage.set(GUIDE_HELLO_KEY, '1'), undefined);
      resetIdle();
      schedule();
    },

    destroy(): void {
      mounted = false;
      for (const key of Object.keys(timers)) stop(key);
      for (const release of stops.splice(0, stops.length)) safe(release, undefined);
      if (frame !== undefined && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
      frame = undefined;
      clearSpot();
      markRow(undefined);
      root.remove?.();
    },

    focusOption,

    valuesChanged(values: Values): void {
      if (!mounted) return;
      const next = { ...(values ?? {}) };
      const changed = changedKeys(snapshot, next);
      const previous = snapshot;
      snapshot = next;
      resetIdle();
      if (off) return;

      const last = changed[changed.length - 1];
      if (last !== undefined) {
        if (last === PLACEMENT_KEY) {
          apply({
            type: 'changed',
            optionId: PLACEMENT_KEY,
            key: 'changed.moved',
            elementId: movedElementId(previous, next),
          });
        } else {
          explainedOptionId = last;
          const option = optionsById.get(last);
          const bound = boundElement(last);
          const typing = option?.type === 'text' || option?.type === 'textarea';
          apply({
            type: 'changed',
            optionId: last,
            key: changedTextKey(option, bound?.property),
            elementId: bound?.elementId,
            typing,
          });
          trackOption(last);
        }
      }
      apply({ type: 'completeness', complete: isComplete() });
    },

    photoState(optionId: string, photo: GuidePhotoState): void {
      apply({
        type: 'photo',
        optionId,
        phase: photo.phase,
        message: photo.message,
        elementId: photo.phase === 'ready' ? boundElement(optionId)?.elementId : undefined,
      });
    },

    step(info: GuideStepInfo): void {
      clearSpot();
      explainedOptionId = undefined;
      stepIndex = info.index;
      if (off || !mounted) return;
      const only = info.optionIds?.length === 1 ? info.optionIds[0] : undefined;
      apply({
        type: 'step',
        index: info.index,
        total: info.total,
        title: info.title,
        optionId: info.optionIds?.[0],
        elementId: info.optionIds?.[0] ? boundElement(info.optionIds[0])?.elementId : undefined,
      });
      // A step with one question on it: the step number first, then what the
      // question actually does, a beat later so it is read as a second thought.
      const option = only ? optionsById.get(only) : undefined;
      if (!option) return;
      later('step', 600, () => {
        const copy = guideTextFor(option, {
          texts,
          formatPrice: options.formatPrice,
          audioProfile: options.product?.audioProfile,
        });
        setBubble(bubbleText({ ...copy, line: `${state.text} ${copy.line}` }, BUBBLE_LIMIT + 40));
      });
    },

    missing(optionIds: string[]): void {
      const first = optionIds?.[0];
      if (!first) return;
      apply({ type: 'missing', optionId: first, label: optionsById.get(first)?.label ?? '' });
    },

    done(): void {
      apply({ type: 'done' });
      // The wizard says "click Save"; everything after it (the cart commit on
      // the page itself) says "add to cart".
      stepIndex = null;
    },

    relayout(): void {
      schedule();
    },
  };
}
